import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
} from "@stripe/react-connect-js";
import {
  stripeConnectKYCControllerSession,
  useStripeConnectKYCControllerStatus,
} from "@/generated/api/api";
import { Alert, AlertIcon, AlertText } from "@/components/ui/alert";
import { CheckCircle, Info } from "lucide-react-native";
import { Spinner } from "@/components/ui/spinner";

export default function StripeConnectIndex() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const [result, setResult] = useState<"error" | "success">();

  const { mutate: getStatus, data: lastStatus } = useStripeConnectKYCControllerStatus();

  // We use `useState` to ensure the Connect instance is only initialized once
  const [stripeConnectInstance] = useState(() => {
    const fetchClientSecret = async () => {
      try {
        const response = await stripeConnectKYCControllerSession({ token });
        return response.token;
      } catch (e) {
        console.error("Error happened during session refresh", e);
        return undefined as any;
      }
    };

    return loadConnectAndInitialize({
      publishableKey: process.env.EXPO_PUBLIC_STRIPE_CONNECT_PK!,
      fetchClientSecret,
    });
  });

  const onExit = useCallback(() => {
    getStatus(
      {
        data: {
          token,
        },
      },
      {
        onError: (e) => {
          console.error("Status failed", e);
          setResult("error");
        },
        onSuccess: (d) => {
          if (d.status === "PASSED") {
            setResult("success");
          }
        },
      }
    );
  }, [getStatus]);

  useEffect(() => {
    onExit();
  }, [onExit]);

  return result === "success" ? (
    <Alert action="success" variant="solid" className="flex-col">
      <AlertIcon as={CheckCircle} className="min-w-5" />
      <AlertText>
        Information has been submitted. Please note, verification check may take
        a while. You may now safely close the window.
      </AlertText>
    </Alert>
  ) : result === "error" ? (
    <Alert action="error" variant="solid" className="flex-col">
      <AlertIcon as={Info} className="min-w-5" />
      <AlertText>
        Something went wrong, please try again later or contact our support
      </AlertText>
    </Alert>
  ) : lastStatus === undefined ? (
    <Spinner size="large" />
  ) : (
    <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
      <ConnectAccountOnboarding
        onExit={onExit}
        collectionOptions={{
          futureRequirements: "include",
          fields: "eventually_due",
        }}
      />
    </ConnectComponentsProvider>
  );
}
